/**
 * Export analysis data (measurements, dosing, events) as a styled .xlsx file.
 *
 * Uses xlsx-js-style for cell formatting (colored header rows, bold text).
 * Follows the same lazy-loading pattern as httmExcelExport.ts.
 */
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { buildExportFilename } from '@/hooks/useExportImage';
import {
  formatParameterLabel,
  getParameterConfig,
} from '@/features/analysis/constants';
import type { MeasurementPoint, DosingPoint, TaskEvent } from '@/hooks/useAnalysisData';

/** Teal brand color for header backgrounds (matches app button backgrounds) */
const HEADER_BG_COLOR = '178A82';
const HEADER_FONT_COLOR = 'FFFFFF';
const FOOTER_FONT_COLOR = 'B0BEC5';

/** Shared header cell style */
const headerStyle = {
  fill: { fgColor: { rgb: HEADER_BG_COLOR } },
  font: { bold: true, color: { rgb: HEADER_FONT_COLOR }, sz: 11 },
  alignment: { horizontal: 'center' as const },
};

/** Footer cell style (subtle gray) */
const footerStyle = {
  font: { color: { rgb: FOOTER_FONT_COLOR }, italic: true, sz: 10 },
};

export interface AnalysisExportParams {
  measurements: Record<string, MeasurementPoint[]>;
  dosing: Record<string, DosingPoint[]>;
  events: TaskEvent[];
  selectedParameters: string[];
  selectedDosing: string[];
  selectedEvents: string[];
  dateRange: { start: string; end: string };
  tankName?: string;
}

/**
 * Export selected analysis data as a styled .xlsx file.
 *
 * Creates up to 3 sheets (Measurements, Dosing, Events) depending on what is selected.
 * Each sheet has a styled header row (teal background, white bold text) and
 * a footer row with "Exported from ReefDataHub.com" + timestamp.
 */
export async function exportAnalysisExcel(params: AnalysisExportParams): Promise<void> {
  const {
    measurements,
    dosing,
    events,
    selectedParameters,
    selectedDosing,
    selectedEvents,
    dateRange,
    tankName,
  } = params;

  // Lazy-load xlsx-js-style for code splitting
  const XLSX = await import('xlsx-js-style');

  const wb = XLSX.utils.book_new();
  let sheetsAdded = 0;

  // --- Measurements Sheet ---
  if (selectedParameters.length > 0) {
    const sheet = buildMeasurementsSheet(XLSX, measurements, selectedParameters, dateRange);
    XLSX.utils.book_append_sheet(wb, sheet, 'Measurements');
    sheetsAdded++;
  }

  // --- Dosing Sheet ---
  if (selectedDosing.length > 0) {
    const sheet = buildDosingSheet(XLSX, dosing, selectedDosing, dateRange);
    XLSX.utils.book_append_sheet(wb, sheet, 'Dosing');
    sheetsAdded++;
  }

  // --- Events Sheet ---
  if (selectedEvents.length > 0) {
    const sheet = buildEventsSheet(XLSX, events, selectedEvents, dateRange);
    XLSX.utils.book_append_sheet(wb, sheet, 'Events');
    sheetsAdded++;
  }

  if (sheetsAdded === 0) return;

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const name = tankName ? `analysis-${tankName}` : 'analysis';
  saveAs(blob, buildExportFilename(name, 'xlsx'));
}

import type { WorkSheet, ColInfo } from 'xlsx-js-style';
import type * as XLSXTypes from 'xlsx-js-style';

type XLSXModule = typeof XLSXTypes;

/** Apply header styling, footer rows, and column widths to a worksheet. */
function applySheetFormatting(
  XLSX: XLSXModule,
  ws: WorkSheet,
  rows: (string | number | null)[][],
  headerCount: number,
  colWidths: ColInfo[],
): void {
  // Style header row
  for (let col = 0; col < headerCount; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
    if (ws[cellRef]) {
      ws[cellRef].s = headerStyle;
    }
  }

  // Style footer rows (last 2 rows are footer)
  const footerStartRow = rows.length - 2;
  for (let r = footerStartRow; r < rows.length; r++) {
    const cellRef = XLSX.utils.encode_cell({ r, c: 0 });
    if (ws[cellRef]) {
      ws[cellRef].s = footerStyle;
    }
  }

  ws['!cols'] = colWidths;
}

/** Append standard footer rows (branding + date range) to the data array. */
function appendFooterRows(
  rows: (string | number | null)[][],
  dateRange: { start: string; end: string },
): void {
  rows.push([]);
  rows.push([`Exported from ReefDataHub.com — ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`]);
  rows.push([`Date range: ${dateRange.start} to ${dateRange.end}`]);
}

/**
 * Build the Measurements sheet in wide format:
 * Date/Time | Param1 (unit) | Param2 (unit) | ...
 */
function buildMeasurementsSheet(
  XLSX: XLSXModule,
  measurements: Record<string, MeasurementPoint[]>,
  selectedParameters: string[],
  dateRange: { start: string; end: string },
): WorkSheet {
  // Build column headers
  const headers = ['Date/Time'];
  for (const key of selectedParameters) {
    const config = getParameterConfig(key.split(':')[0]!);
    const label = formatParameterLabel(key);
    headers.push(config.unit ? `${label} (${config.unit})` : label);
  }

  // Collect all unique timestamps across selected parameters
  const timestampSet = new Set<string>();
  for (const key of selectedParameters) {
    const points = measurements[key];
    if (points) {
      for (const point of points) {
        timestampSet.add(point.timestamp);
      }
    }
  }

  // Sort timestamps chronologically
  const timestamps = [...timestampSet].sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );

  // Build lookup maps for quick value retrieval: key -> timestamp -> value
  const lookups = new Map<string, Map<string, number>>();
  for (const key of selectedParameters) {
    const map = new Map<string, number>();
    const points = measurements[key];
    if (points) {
      for (const point of points) {
        map.set(point.timestamp, point.value);
      }
    }
    lookups.set(key, map);
  }

  // Build data rows
  const rows: (string | number | null)[][] = [headers];
  for (const ts of timestamps) {
    const row: (string | number | null)[] = [format(new Date(ts), 'yyyy-MM-dd HH:mm')];
    for (const key of selectedParameters) {
      const value = lookups.get(key)?.get(ts);
      row.push(value !== undefined ? value : null);
    }
    rows.push(row);
  }

  appendFooterRows(rows, dateRange);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  applySheetFormatting(XLSX, ws, rows, headers.length, [
    { wch: 18 },
    ...selectedParameters.map(() => ({ wch: 22 })),
  ]);

  return ws;
}

/**
 * Build the Dosing sheet:
 * Date | Chemical1 (ml) | Chemical2 (ml) | ...
 */
function buildDosingSheet(
  XLSX: XLSXModule,
  dosing: Record<string, DosingPoint[]>,
  selectedDosing: string[],
  dateRange: { start: string; end: string },
): WorkSheet {
  const headers = ['Date'];
  for (const name of selectedDosing) {
    headers.push(`${name} (ml)`);
  }

  // Collect all unique dates
  const dateSet = new Set<string>();
  for (const name of selectedDosing) {
    const points = dosing[name];
    if (points) {
      for (const point of points) {
        dateSet.add(point.date);
      }
    }
  }

  const dates = [...dateSet].sort();

  // Build lookup maps
  const lookups = new Map<string, Map<string, number>>();
  for (const name of selectedDosing) {
    const map = new Map<string, number>();
    const points = dosing[name];
    if (points) {
      for (const point of points) {
        map.set(point.date, point.volume_ml);
      }
    }
    lookups.set(name, map);
  }

  const rows: (string | number | null)[][] = [headers];
  for (const date of dates) {
    const row: (string | number | null)[] = [date];
    for (const name of selectedDosing) {
      const value = lookups.get(name)?.get(date);
      row.push(value !== undefined ? value : null);
    }
    rows.push(row);
  }

  appendFooterRows(rows, dateRange);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  applySheetFormatting(XLSX, ws, rows, headers.length, [
    { wch: 12 },
    ...selectedDosing.map(() => ({ wch: 22 })),
  ]);

  return ws;
}

/**
 * Build the Events sheet:
 * Date | Event Type | Task Type | Notes
 */
function buildEventsSheet(
  XLSX: XLSXModule,
  events: TaskEvent[],
  selectedEvents: string[],
  dateRange: { start: string; end: string },
): WorkSheet {
  const headers = ['Date', 'Event Type', 'Task Type', 'Notes'];

  // Filter events to selected types
  const selectedSet = new Set(selectedEvents);
  const filtered = events
    .filter((e) => selectedSet.has(e.type))
    .sort((a, b) => a.date.localeCompare(b.date));

  const rows: (string | number | null)[][] = [headers];
  for (const event of filtered) {
    rows.push([event.date, event.type, event.task_type, event.notes || '']);
  }

  appendFooterRows(rows, dateRange);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  applySheetFormatting(XLSX, ws, rows, headers.length, [
    { wch: 12 },
    { wch: 25 },
    { wch: 18 },
    { wch: 40 },
  ]);

  return ws;
}
